import { useEffect, useLayoutEffect, useState } from 'react';
import { VEHICLE_MODEL } from '../../../../../models/Vehicle';
import HandleChange from '../../../../../Helpers/HandleChange';
import { toast } from 'react-toastify';
import {
    CreateVehicleEmployee,
    GetVehicleStatus,
    GetVehicleTags,
} from '../../../../../reduxToolkit/Vehicle/VehicleApi';
import { CreateVehicleAndRelationSupplier } from '../../../../../reduxToolkit/Providers/providersApi';
import { CreateVehicleAndRelationContractor } from '../../../../../reduxToolkit/Contractor/ContractorApi';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from '../../../../../Routes/routes';
import { getPhotoPicker, PHOTO_PICKER_MODEL } from '../../../../../components/PhotoPicker/SubComponent/Models/Models';
import defaultImages from '../../../../../assets/ImagesDefaults/ic-img-default-vehicle.svg';
import { errorMsg, isNumber } from '../../../../../Apis/functions';
import { TABS_VEHICLE_CREATE } from '../model/model';

const CreateVehicleApp = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentTime = new Date();
    const { t } = useTranslation();
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const [isValid, setIsValid] = useState(true);
    const [emptyFields, setEmptyFields] = useState(false);
    const [item, setItem] = useState({ ...VEHICLE_MODEL });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [contractorId, setContractorId] = useState(sessionStorage.getItem('contractorId'));
    const [supplierId, setSupplierId] = useState(sessionStorage.getItem('providerId'));
    const [photoPicker, setPhotoPicker] = useState({ ...PHOTO_PICKER_MODEL, preview: defaultImages });
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);

    // Loads vehicle statuses and tags on mount.
    useEffect(() => {
        dispatch(GetVehicleStatus());
        dispatch(GetVehicleTags());
    }, []);

    // Initializes step labels based on translations.
    useLayoutEffect(() => {
        setSteps([
            t(TABS_VEHICLE_CREATE.DETAILS.TRANS),
            t(TABS_VEHICLE_CREATE.PHOTO.TRANS),
            t(TABS_VEHICLE_CREATE.SUMMARY.TRANS),
        ]);
    }, [permission, t]);

    // Handles vehicle creation depending on the current context
    const handleCreate = () => {
        if (!item.isValid() || !isValid) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        const imageFile = getPhotoPicker(photoPicker);
        let vehicleData = item.toRegister();

        if (contractorId !== null) {
            dispatch(CreateVehicleAndRelationContractor({ vehicleData: vehicleData, imageFile })).then(
                ({
                    payload: {
                        data: { code, success },
                    },
                }) => {
                    if (success) {
                        toast.success(t('has_been_created_successfully'));
                        navigate(routes.vehicleContractor.search);
                    } else {
                        errorMsg(code);
                    }
                },
            );
        } else if (supplierId !== null) {
            dispatch(CreateVehicleAndRelationSupplier({ vehicleData: vehicleData, imageFile })).then(
                ({
                    payload: {
                        data: { code, success },
                    },
                }) => {
                    if (success) {
                        toast.success(t('has_been_created_successfully'));
                        navigate(routes.vehicleSupplier.list);
                    } else {
                        errorMsg(code);
                    }
                },
            );
        } else {
            dispatch(CreateVehicleEmployee({ vehicleData: vehicleData, imageFile })).then(
                ({
                    payload: {
                        data: { code, success },
                    },
                }) => {
                    if (success) {
                        toast.success(t('has_been_created_successfully'));
                        navigate(routes.vehicleFleet.list);
                    } else {
                        errorMsg(code);
                    }
                },
            );
        }
    };

    // Validates the vehicle model year.
    function validateModel(value) {
        if (!isNumber(value)) {
            setIsValid(true);
            return true;
        }
        if (!value || value < 1950 || value > currentTime.getFullYear()) {
            setIsValid(false);
            return false;
        }
        setIsValid(true);
        return true;
    }

    // Handles input changes and triggers model-year validation when needed.
    function handleOnChange(evt) {
        const name = evt.target.name;
        const value = typeof evt.target === 'undefined' ? evt : evt.target.value;
        if (name === 'model') {
            validateModel(parseInt(value));
        }
        handleChange(evt);
    }

    // Resets the form and photo picker to initial values.
    const resetForm = () => {
        setItem(VEHICLE_MODEL);
        setPhotoPicker({ ...PHOTO_PICKER_MODEL, preview: defaultImages });
    };

    // Updates the active step in the stepper.
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    /**
     * Function to validate.
     * @returns {boolean}
     */
    const handleStepSetUp = () => {
        if (!item.isValid() || !isValid) {
            setEmptyFields(true);
            return false;
        }
  
        return true;
    };
    
    return {
        contractorId,
        supplierId,
        emptyFields,
        isValid,
        currentTime,
        item,
        setItem,
        photoPicker,
        setPhotoPicker,
        activeStep,
        setActiveStep,
        steps,
        handleStepChange,
        handleStepSetUp,
        resetForm: () => resetForm(),
        onChange: (evt, name, child, subChild) => handleOnChange(evt, name, child, subChild),
        handleCreate: () => handleCreate(),
    };
};

export default CreateVehicleApp;
